<script setup>
// Vue Imports
import { computed, reactive, ref, watch } from "vue"
import { useRoute, useRouter } from "vue-router"
import { required, email, minLength, sameAs, helpers } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'

// Component Imports
import AtomButton from "@Atoms/Button.vue"

// Utils Imports
import { storeCurrentSession } from "@/utils/cognito"
import { evaluateStrength } from "@/utils/passwordStrength"
import {corporateRegex} from "@/utils/corporateClients"

// Other Imports
import { signUp } from 'aws-amplify/auth'
import useAlert from "@/hooks/alert"
import { XCircleIcon, CheckCircleIcon } from '@heroicons/vue/24/solid'
import { useStoreClientSource } from "@/stores/storeClientSource"

import { APP } from "@/config/app"
import { retrieveTrafficSource } from '@/utils/utmTracking.js';
import App from "@/App.vue";

// Component Logic
const router = useRouter()
const route = useRoute()

const storeClientSource = useStoreClientSource()
const regex = corporateRegex

// reference to data input fields
const registerForm = reactive({
  firstname: '',
  middlename: '',
  lastname: '',
  email: '',
  password: '',
  confirmPassword: '',
  marketing: null,
})

const isShowing = ref(false)

let loading = ref(false)

//sets up hook for message alerting
const { message, messageStyleType, messageContainsHtml, setMessage } = useAlert()

// set the validation rules
const registerFormRules = computed(() => {
  return {
    firstname: {
      required: helpers.withMessage('Please enter your first name', required),
    },
    lastname: {
      required: helpers.withMessage('Please enter your last name', required),
    },
    email: {
      required: helpers.withMessage('Please enter your email address', required),
      email: helpers.withMessage('Please enter a valid email address', email),
    },
    password: {
      required: helpers.withMessage('Please enter a password', required),
      minLength: helpers.withMessage('Your password should have 7 or more characters', minLength(7)),
    },
    confirmPassword: {
      required: helpers.withMessage('Please enter the password again. ', required),
      minLength: helpers.withMessage('Your password should have 7 or more characters', minLength(7)),
      sameAs: helpers.withMessage('Please ensure that this matches your password', sameAs(registerForm.password)),
    },
    marketing: {
      required: helpers.withMessage('Please select whether you would like to receive marketing communications from us.', required),
    },
  }
})
const v$ = useVuelidate(registerFormRules, registerForm)

// if there is a valid 'src' query string parameter, add it to the store
if(route.query.src !== undefined) {
  if (regex.test(route.query.src.toString())) {
    storeClientSource.saveClientSource(route.query.src.toString())
  }
}

// calls register method
async function register() {
  // runs form validation code
  const validationResult = await v$.value.$validate()
  if (!validationResult) {
    return
  }

  loading.value = true
  await handleSignUp()
}

async function handleSignUp() {
  const clientSource = {
    corporateId: storeClientSource.getClientSource() ?? null,
    ...retrieveTrafficSource(),
  };

  if(route.query.src !== undefined && regex.test(route.query.src.toString())){
    clientSource.corporateId = route.query.src.toString();
    storeClientSource.saveClientSource(route.query.src.toString())
  }

  try {
    const { isSignUpComplete, userId, nextStep } = await signUp({
      username: registerForm.email.toLowerCase(),
      password: registerForm.password,
      options: {
        userAttributes: {
          email: registerForm.email.toLowerCase(),
          given_name: registerForm.firstname,
          middle_name: registerForm.middlename,
          family_name: registerForm.lastname,
          "custom:client_group": "SELF_SELECT",
          "custom:client_source": JSON.stringify(clientSource),
        },
      }
    })

    //signUpStep:
    // "CONFIRM_SIGN_UP" - The sign up needs to be confirmed by collecting a code from the user and calling confirmSignUp.
    // "DONE" - The sign up process has been fully completed.
    // "COMPLETE_AUTO_SIGN_IN" - The sign up process needs to complete by invoking the autoSignIn API.

    loading.value = false

    switch(nextStep.signUpStep) {
      case "CONFIRM_SIGN_UP":
        await router.replace({
          name: "Confirm",
          query: {
            username: registerForm.email,
            marketing: registerForm.marketing
          },
        })

        break;
      case "COMPLETE_AUTO_SIGN_IN":
        break;
      case "DONE":
        // Update the state with the session tokens
        await storeCurrentSession();

        await router.replace({
          name: "Dashboard",
        })

        break;
      default:
        console.log(nextStep.signUpStep)
    }

  } catch (error) {
    if (showExistingClientForm.value && error?.name === "UsernameExistsException") {
      const url = `${APP.MyWealthPrivateWebsite}/your-share-maturity?company_name=bt`
      error += `<br>If you are an existing my wealth private client, please <a href="${url}">click here</a> to continue`
    }

    setMessage(error, "danger", true)

    loading.value = false
  }
}

/* Password strength */
const passwordChecks = reactive({
  characters: false,
  numbers: false,
  uppercase: false,
  lowercase: false,
  special: false
})
const passwordStrength = computed(() => {
  return evaluateStrength(registerForm.password)
})

watch(() => registerForm.password, password => {
  passwordChecks.characters = password.length > 7 && password.length < 51
  passwordChecks.numbers = /\d/.test(password)
  passwordChecks.uppercase = /[A-Z]/.test(password)
  passwordChecks.lowercase = /[a-z]/.test(password)
  passwordChecks.special = /[^\w\s]/.test(password)
})

const showExistingClientForm = computed(() => ([APP.BtClientSource].includes(storeClientSource.getClientSource())))
</script>

<template>
  <section class="bg-sandstone dark:bg-teal py-20 lg:py-28 px-10 lg:px-0">
    <div class="mx-auto max-w-7xl flex flex-col items-center justify-center gap-12">
      <base-message
        v-if="message"
        :type="messageStyleType"
        :message="messageContainsHtml ? message : null"
        class="sm:mx-auto sm:w-full sm:max-w-lg"
      >
        {{ message }}
      </base-message>

      <div class="text-center flex flex-col gap-8">
        <h1 class="text-off-black dark:text-white">
          Register
        </h1>
        <p class="text-center body-2-light text-off-black dark:text-white">
          Complete your details below to create your Self-Select Service log in
        </p>

        <base-message v-if="showExistingClientForm" type="info">
          If you are an existing my wealth private client, please click <a :href="`${APP.MyWealthPrivateWebsite}/your-share-maturity?company_name=bt`">here</a> to continue
        </base-message>
      </div>

      <div class="flex flex-col space-y-8 lg:w-1/3">
        <form @submit.prevent="register" class="flex flex-col space-y-8 body-3-reg">
          <div>
            <input
              type="text"
              class="login-form-input body-3-reg"
              id="firstname"
              placeholder="First name"
              v-model.trim="registerForm.firstname"
              autocomplete
              required
            >
            <ul v-if="v$.firstname.$errors.length" class="my-3 rounded-l bg-red-50 p-3">
              <li v-for="error in v$.firstname.$errors" :key="error.$uid" class="text-red-400">
                {{ error.$message }}
              </li>
            </ul>
          </div>

          <div>
            <input
                type="text"
                class="login-form-input body-3-reg"
                id="middlename"
                placeholder="Middle name"
                v-model.trim="registerForm.middlename"
            >
            <div v-if="v$.middlename?.$errors?.length" class="my-3 rounded-l bg-red-50 p-3">
              <span v-for="error in v$.middlename?.$errors" :key="error.$uid" class="text-red-400">
                {{ error.$message }}
              </span>
            </div>
          </div>

          <div>
            <input
              type="text"
              class="login-form-input body-3-reg"
              id="lastname"
              placeholder="Last name"
              v-model.trim="registerForm.lastname"
              autocomplete
              required
            >
            <ul v-if="v$.lastname.$errors.length" class="my-3 rounded-l bg-red-50 p-3">
              <li v-for="error in v$.lastname.$errors" :key="error.$uid" class="text-red-400">
                {{ error.$message }}
              </li>
            </ul>
          </div>

          <div>
            <input
              type="text"
              class="login-form-input body-3-reg"
              id="email"
              placeholder="Email"
              v-model.trim="registerForm.email"
              autocomplete
              required
            >
            <ul v-if="v$.email.$errors.length" class="my-3 rounded-l bg-red-50 p-3">
              <li v-for="error in v$.email.$errors" :key="error.$uid" class="text-red-400">
                {{ error.$message }}
              </li>
            </ul>
          </div>

          <div>
            <input
              type="password"
              class="login-form-input body-3-reg"
              id="password"
              placeholder="Password"
              v-model.trim="registerForm.password"
              autocomplete="false"
              required
            >
            <ul v-if="v$.password.$errors.length" class="my-3 rounded-l bg-red-50 p-3">
              <li v-for="error in v$.password.$errors" :key="error.$uid" class="text-red-400">
                {{ error.$message }}
              </li>
            </ul>

            <div ref="passwordFilters" class="flex flex-col gap-y-8 body-3-reg mt-8">
              <div class="flex items-center border rounded-md border-granite/50 dark:border-white justify-start overflow-hidden">
                <Transition name="width">
                  <div
                    :class="
                      passwordStrength[0] === 1 ? 'w-[10%] bg-red-400'
                      : passwordStrength[0] === 2 ? 'w-[20%] bg-red-400'
                      : passwordStrength[0] === 3 ? 'w-[30%] bg-red-400'
                      : passwordStrength[0] === 4 ? 'w-[40%] bg-red-400'
                      : passwordStrength[0] === 5 ? 'w-[50%] bg-yellow-400'
                      : passwordStrength[0] === 6 ? 'w-[60%] bg-yellow-400'
                      : passwordStrength[0] === 7 ? 'w-[70%] bg-green-400'
                      : passwordStrength[0] === 8 ? 'w-[80%] bg-green-400'
                      : passwordStrength[0] === 9 ? 'w-full bg-green-600'
                      : 'w-full bg-green-600'
                    "
                    class="px-2 pt-1.5 pb-2 h-[30px] text-xs transition-width ease-in-out duration-300 text-black"
                  >
                    <p class="absolute">
                      {{passwordStrength[1]}}
                    </p>
                  </div>
                </Transition>
              </div>

              <div class="flex flex-col gap-2">
                <div class="flex items-center gap-2 dropdown-content mt-2">
                  <CheckCircleIcon v-if="passwordChecks.characters" class="w-4 text-green-500 mt-1" />
                  <XCircleIcon v-else class="w-4 mt-1" />
                  <p class="text-sm dark:text-white">8 to 50 characters.</p>
                </div>
                <div class="flex items-center gap-2 dropdown-content">
                  <CheckCircleIcon v-if="passwordChecks.numbers" class="w-4 text-green-500 mt-1" />
                  <XCircleIcon v-else class="w-4 mt-1" />
                  <p class="text-sm dark:text-white">1 number or more.</p>
                </div>
                <div class="flex items-center gap-2 dropdown-content">
                  <CheckCircleIcon v-if="passwordChecks.uppercase" class="w-4 text-green-500 mt-1" />
                  <XCircleIcon v-else class="w-4 mt-1" />
                  <p class="text-sm dark:text-white">1 upper case letter or more.</p>
                </div>
                <div class="flex items-center gap-2 dropdown-content">
                  <CheckCircleIcon v-if="passwordChecks.lowercase" class="w-4 text-green-500 mt-1" />
                  <XCircleIcon v-else class="w-4 mt-1" />
                  <p class="text-sm dark:text-white">1 lower case letter or more.</p>
                </div>
                <div class="flex items-center gap-2 dropdown-content mb-2">
                  <CheckCircleIcon v-if="passwordChecks.special" class="w-4 text-green-500 mt-1" />
                  <XCircleIcon v-else class="w-4 mt-1" />
                  <p class="text-sm dark:text-white">1 special character or more. Allowed characters are:<br/> ^ $ * . [ ] { } ( ) ? " ! @ # % &amp; / \ , &gt; &lt; ' : ; | _ ~ ` = + - </p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <input
              type="password"
              class="login-form-input body-3-reg"
              id="confirm-password"
              placeholder="Confirm password"
              v-model.trim="registerForm.confirmPassword"
              autocomplete="false"
              required
            >
            <ul v-if="v$.confirmPassword.$errors.length" class="my-3 rounded-l bg-red-50 p-3">
              <li v-for="error in v$.confirmPassword.$errors" :key="error.$uid" class="text-red-400">
                {{ error.$message }}
              </li>
            </ul>
          </div>

          <div class="text-center text-off-black dark:text-white">
            <div class="mt-2 flex flex-col gap-2 justify-center text-center w-full items-center">
              <p class="max-w-[554px]">
                We will typically send communications by email and/or via your online account, but on occasion we may also contact you by post or telephone.
                <br>
                (Please note that your choice here will not change the communications we will send to you as part of our services to you.)
              </p>
            </div>
            <div class="mt-2 flex flex-col gap-2 justify-center text-center items-center">
                <p class="max-w-[554px]">
                    If you would like to receive marketing communications from us, please tick the appropriate box below.
                </p>
            </div>
            <div class="flex flex-row gap-12 py-3 mt-3 justify-center">
              <div class="flex flex-row gap-6 items-center select-none">
                <input type="radio" id="marketing--yes" name="marketing" :value="true" v-model="registerForm.marketing" class="mt-1"/>
                <label for="marketing--yes">Yes, please</label>
              </div>
              <div class="flex flex-row gap-6 items-center select-none">
                <input type="radio" id="marketing--no" name="marketing" :value="false" v-model="registerForm.marketing" class="mt-1" />
                <label for="marketing--no">No, thanks</label>
              </div>
            </div>
            <ul v-if="v$.marketing.$errors.length" class="my-3 rounded-l bg-red-50 p-3">
              <li v-for="error in v$.marketing.$errors" :key="error.$uid" class="text-red-400">
                {{ error.$message }}
              </li>
            </ul>
          </div>

          <div class="flex flex-row justify-center items-center">
            <AtomButton
              :isLoading="loading"
              type="submit"
              aria-label="Register button"
              button-type="unset"
              :class="[APP.MyWealthMode ? 'primary-mwp' : 'primary-mwi', 'body-3-semi-bold rounded-full max-w-min mx-auto button-padding']"
            >
              Register
            </AtomButton>
          </div>
        </form>

        <p class="text-center body-3-reg text-off-black dark:text-white max-w-[554px]">
          By creating an account, we may use your registration details to contact you regarding this service.
          We will not share your details with other companies for marketing purposes.
          Any personal information you provide to us will be dealt with in line with our
        </p>

        <RouterLink
          to="/privacy-notice"
          target="_blank"
          aria-label="privacy notice button"
          :class="[APP.MyWealthMode ? 'tertiary-mwp' : 'tertiary-mwi', 'body-3-semi-bold underline mx-auto']"
        >
          Privacy Notice
        </RouterLink>

        <RouterLink
          to="/terms-conditions"
          target="_blank"
          aria-label="terms conditions button"
          :class="[APP.MyWealthMode ? 'tertiary-mwp' : 'tertiary-mwi', 'body-3-semi-bold underline mx-auto']"
        >
          Terms and Conditions
        </RouterLink>
      </div>
    </div>
  </section>
</template>
